import { memo, useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { Button, Input, InputNumber, DatePicker, Switch, message, Spin, Tooltip } from 'antd';
import {
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  UserOutlined,
} from '@ant-design/icons';
import { geocollectItemPropertiesModeAtom } from '../../controllers/popups/CollectItemPopup';
import { CollectItem_GetOne_Response } from '~/common/typescript/apis/CollectItemAPIs';
import { CollectFormAPIs } from '~/common/typescript/apis/CollectFormAPIs';
import {
  allCollectFormsMapAtom,
  collectFormFieldsMapAtom,
  singleFormsCacheAtom,
} from '../../panels/GeocollectDatatablePanel/atoms/CollectFormAtoms';
import { projectMembersMapAtom } from '../../Member/MemberStore';
import { parseFormContent, ParsedFormField } from '../../panels/GeocollectDatatablePanel/utils/CollectFormParser';
import FieldTypeIcon from '../../panels/GeocollectDatatablePanel/utils/FieldTypeIcon';
import DateTimeViewer from '~/components/common/DateTimeViewer';
import dayjs from 'dayjs';

interface GeocollectItemPropertiesProps {
  collectItemData: CollectItem_GetOne_Response | null;
  collectFormUuid?: string;
  loading?: boolean;
  onSave?: (properties: Record<string, any>) => Promise<void>;
}

const GeocollectItemProperties = ({
  collectItemData,
  collectFormUuid,
  loading,
  onSave,
}: GeocollectItemPropertiesProps) => {
  const { t } = useTranslation();
  const [mode, setMode] = useAtom(geocollectItemPropertiesModeAtom);
  const [editedProperties, setEditedProperties] = useState<Record<string, any>>({});
  const [saving, setSaving] = useState(false);

  const allFormsMap = useAtomValue(allCollectFormsMapAtom);
  const formFieldsMap = useAtomValue(collectFormFieldsMapAtom);
  const projectMembersMap = useAtomValue(projectMembersMapAtom);
  const setSingleFormsCache = useSetAtom(singleFormsCacheAtom);

  const activeFormUuid = collectFormUuid || collectItemData?.collect_form_uuid;
  const activeFormData = activeFormUuid ? allFormsMap[activeFormUuid] : null;

  // Fallback: fetch form schema if missing from cache
  useEffect(() => {
    if (activeFormUuid && !allFormsMap[activeFormUuid]) {
      CollectFormAPIs.remote_getOne(activeFormUuid)
        .then((formData) => {
          setSingleFormsCache((prev) => ({ ...prev, [activeFormUuid]: formData }));
        })
        .catch((err) => {
          console.error('Error fetching collect form schema for properties:', err);
        });
    }
  }, [activeFormUuid, allFormsMap, setSingleFormsCache]);

  // Extract form field definitions
  const fieldsMap = useMemo(() => {
    if (!activeFormUuid) return {};
    if (formFieldsMap[activeFormUuid]) return formFieldsMap[activeFormUuid];
    if (activeFormData) {
      const parsed = parseFormContent(activeFormData);
      if (parsed?.fields) {
        const map: Record<string, ParsedFormField> = {};
        parsed.fields.forEach((f) => {
          if (f.key) map[f.key] = f;
        });
        return map;
      }
    }
    return {};
  }, [activeFormUuid, formFieldsMap, activeFormData]);

  // Initialize edited properties when data changes
  useEffect(() => {
    if (collectItemData?.properties) {
      setEditedProperties({ ...collectItemData.properties });
    }
  }, [collectItemData?.properties]);

  const handleEdit = () => {
    setMode('EDIT');
  };

  const handleCancel = () => {
    // Reset to original values
    if (collectItemData?.properties) {
      setEditedProperties({ ...collectItemData.properties });
    }
    setMode('VIEW');
  };

  const handleSave = async () => {
    if (!onSave) {
      message.error('Chức năng lưu chưa được cấu hình');
      return;
    }

    try {
      setSaving(true);
      await onSave(editedProperties);
      message.success('Đã lưu thay đổi thành công');
      setMode('VIEW');
    } catch (error) {
      console.error('Error saving properties:', error);
      message.error('Không thể lưu thay đổi');
    } finally {
      setSaving(false);
    }
  };

  const handlePropertyChange = (key: string, value: any) => {
    setEditedProperties((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // Order property keys based on form definition
  const orderedKeys = useMemo(() => {
    const rawKeys = Object.keys(editedProperties).filter(
      (k) => k !== 'uuid' && k !== 'collect_form_uuid'
    );

    if (!activeFormData) return rawKeys;

    const parsed = parseFormContent(activeFormData);
    const formFieldKeys = (parsed?.fields || []).map((f) => f.key);

    const ordered: string[] = [];
    formFieldKeys.forEach((k) => {
      if (rawKeys.includes(k)) {
        ordered.push(k);
      }
    });

    rawKeys.forEach((k) => {
      if (!ordered.includes(k)) {
        ordered.push(k);
      }
    });

    return ordered;
  }, [editedProperties, activeFormData]);

  const getPropertyLabel = (key: string, fieldDef?: ParsedFormField) => {
    if (fieldDef?.label) return fieldDef.label;
    if (key === 'user_id' || key === 'collector_id') {
      return t('collect__collector', 'Người thu thập');
    }
    if (key === 'created_at_ts' || key === 'created_at' || key === 'client_created_at') {
      return t('collect__created_at', 'Thời gian thu thập');
    }
    return key;
  };

  // Detect field type based on schema, key name, and value
  const detectFieldType = (
    key: string,
    value: any,
    fieldDef?: ParsedFormField
  ): 'date' | 'number' | 'boolean' | 'text' => {
    if (fieldDef?.type) {
      const type = fieldDef.type.toLowerCase();
      if (type === 'date' || type === 'datetime' || type === 'time') return 'date';
      if (type === 'number' || type === 'integer' || type === 'float') return 'number';
      if (type === 'boolean' || type === 'checkbox') return 'boolean';
    }

    const lowerKey = key.toLowerCase();
    if (
      lowerKey.includes('date') ||
      lowerKey.includes('time') ||
      lowerKey.endsWith('_at') ||
      lowerKey.endsWith('_ts')
    ) {
      if (typeof value === 'number') {
        return 'date';
      }
      if (typeof value === 'string' && !isNaN(Date.parse(value))) {
        return 'date';
      }
    }

    if (typeof value === 'boolean') {
      return 'boolean';
    }

    if (typeof value === 'number') {
      return 'number';
    }

    return 'text';
  };

  const renderEditInput = (key: string, value: any, fieldDef?: ParsedFormField) => {
    const fieldType = detectFieldType(key, value, fieldDef);

    switch (fieldType) {
      case 'date': {
        const numVal = Number(value);
        let d = value;
        if (!isNaN(numVal) && numVal > 0) {
          d = numVal < 100000000000 ? numVal * 1000 : numVal;
        }
        const dateValue = dayjs(d);
        return (
          <UI_DatePicker
            value={dateValue.isValid() ? dateValue : null}
            onChange={(date) => {
              handlePropertyChange(key, date ? date.valueOf() : null);
            }}
            showTime
            format="DD/MM/YYYY HH:mm:ss"
            placeholder="Chọn ngày giờ"
          />
        );
      }

      case 'number':
        return (
          <UI_InputNumber
            value={value}
            onChange={(newValue) => {
              handlePropertyChange(key, newValue);
            }}
            style={{ width: '100%' }}
          />
        );

      case 'boolean':
        return (
          <Switch
            checked={!!value}
            onChange={(checked) => {
              handlePropertyChange(key, checked);
            }}
          />
        );

      case 'text':
      default:
        return (
          <UI_Input
            value={typeof value === 'object' ? JSON.stringify(value) : String(value ?? '')}
            onChange={(e) => {
              let newValue: any = e.target.value;
              if (typeof value === 'object') {
                try {
                  newValue = JSON.parse(e.target.value);
                } catch {
                  // Fallback to string if parse fails
                }
              }
              handlePropertyChange(key, newValue);
            }}
          />
        );
    }
  };

  const renderViewValue = (key: string, value: any, fieldDef?: ParsedFormField) => {
    if (value === null || value === undefined) return <span style={{ color: '#94a3b8' }}>-</span>;

    // Resolve user collector
    if (key === 'user_id' || key === 'collector_id') {
      const numId = Number(value);
      const user = !isNaN(numId) ? projectMembersMap[numId] : null;
      if (user) {
        return (
          <UI_UserBadge>
            <UserOutlined style={{ color: '#3b82f6', fontSize: 13 }} />
            <span>{user.fullname || user.username || `User #${value}`}</span>
          </UI_UserBadge>
        );
      }
      return (
        <UI_UserBadge>
          <UserOutlined style={{ color: '#94a3b8', fontSize: 13 }} />
          <span>User #{value}</span>
        </UI_UserBadge>
      );
    }

    if (key === 'created_at_ts') {
      const sec = Number(value);
      const ms = !isNaN(sec) ? (sec < 100000000000 ? sec * 1000 : sec) : value;
      return <DateTimeViewer date={ms} initialMode="relative" />;
    }

    const fieldType = detectFieldType(key, value, fieldDef);

    switch (fieldType) {
      case 'date': {
        const numVal = Number(value);
        let dateVal = value;
        if (!isNaN(numVal) && numVal > 0) {
          dateVal = numVal < 100000000000 ? numVal * 1000 : numVal;
        }
        return <DateTimeViewer date={dateVal} initialMode="relative" />;
      }

      case 'number':
        return (
          <UI_Properties_Value $align="right" style={{ fontFamily: 'monospace' }}>
            {value.toLocaleString('vi-VN')}
          </UI_Properties_Value>
        );

      case 'boolean':
        return (
          <UI_BooleanBadge $checked={!!value}>
            {value ? (
              <>
                <CheckCircleFilled style={{ color: '#10b981', fontSize: 14 }} />
                <span>Có</span>
              </>
            ) : (
              <>
                <CloseCircleFilled style={{ color: '#ef4444', fontSize: 14 }} />
                <span>Không</span>
              </>
            )}
          </UI_BooleanBadge>
        );

      case 'text':
      default:
        return (
          <UI_Properties_Value>
            {typeof value === 'object' ? JSON.stringify(value) : String(value)}
          </UI_Properties_Value>
        );
    }
  };

  if (loading) {
    return (
      <UI_LoadingWrapper>
        <Spin size="small" />
        <span style={{ marginLeft: 8 }}>Đang tải thông tin...</span>
      </UI_LoadingWrapper>
    );
  }

  if (!collectItemData?.properties || orderedKeys.length === 0) {
    return <UI_EmptyState>Không có dữ liệu trường thông tin</UI_EmptyState>;
  }

  return (
    <UI_Container>
      {/* Properties List */}
      <UI_Properties_Wrapper>
        {orderedKeys.map((key, index) => {
          const value = editedProperties[key];
          if (value === null || value === undefined) return null;

          const fieldDef = fieldsMap[key];
          const label = getPropertyLabel(key, fieldDef);

          if (mode === 'EDIT') {
            return (
              <UI_Properties_EditRow key={key || index}>
                <UI_Properties_Label title={key}>
                  <FieldTypeIcon type={fieldDef?.type} fieldKey={key} />
                  <span>{label}</span>
                </UI_Properties_Label>
                {renderEditInput(key, value, fieldDef)}
              </UI_Properties_EditRow>
            );
          }

          return (
            <UI_Properties_ViewRow key={key || index}>
              <UI_Properties_Label title={key}>
                <FieldTypeIcon type={fieldDef?.type} fieldKey={key} />
                <span>{label}</span>
              </UI_Properties_Label>
              <UI_ValueContainer>{renderViewValue(key, value, fieldDef)}</UI_ValueContainer>
            </UI_Properties_ViewRow>
          );
        })}
      </UI_Properties_Wrapper>

      {/* Action Buttons */}
      <UI_ActionRow>
        {mode === 'VIEW' ? (
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={handleEdit}
          >
            Chỉnh sửa
          </Button>
        ) : (
          <UI_EditActions>
            <Button
              type="primary"
              size="small"
              icon={<SaveOutlined />}
              onClick={handleSave}
              loading={saving}
            >
              Lưu
            </Button>
            <Button
              size="small"
              icon={<CloseOutlined />}
              onClick={handleCancel}
              disabled={saving}
            >
              Hủy
            </Button>
          </UI_EditActions>
        )}
      </UI_ActionRow>
    </UI_Container>
  );
};

export default memo(GeocollectItemProperties);

const UI_Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const UI_ActionRow = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: 12px;
  border-top: 1px solid #e2e8f0;
`;

const UI_EditActions = styled.div`
  display: flex;
  gap: 8px;
`;

const UI_LoadingWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  font-size: 14px;
  color: #64748b;
`;

const UI_EmptyState = styled.div`
  text-align: center;
  padding: 32px 16px;
  font-size: 13px;
  color: #94a3b8;
  font-style: italic;
  background: white;
  border-radius: 12px;
  border: 1px dashed #cbd5e1;
`;

const UI_Properties_Wrapper = styled.div`
  background: white;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
`;

const UI_Properties_ViewRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 16px;
  border-bottom: 1px solid #f1f5f9;
  transition: background 0.15s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f8fafc;
  }
`;

const UI_Properties_EditRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px 16px;
  border-bottom: 1px solid #f1f5f9;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f8fafc;
  }
`;

const UI_Properties_Label = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #475569;
  font-weight: 500;
  min-width: 110px;
  flex-shrink: 0;

  span {
    word-break: break-word;
  }
`;

const UI_ValueContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  text-align: right;
  flex: 1;
  min-width: 0;
`;

const UI_Properties_Value = styled.div<{ $align?: 'left' | 'right' }>`
  font-size: 13px;
  color: #1e293b;
  word-break: break-word;
  font-weight: 400;
  text-align: ${(props) => props.$align || 'right'};
`;

const UI_UserBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;
  color: #1e293b;
  background: #f8fafc;
  padding: 2px 8px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
`;

const UI_BooleanBadge = styled.div<{ $checked: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 500;
  color: ${(props) => (props.$checked ? '#065f46' : '#991b1b')};
  background: ${(props) => (props.$checked ? '#ecfdf5' : '#fef2f2')};
  padding: 2px 8px;
  border-radius: 6px;
  border: 1px solid ${(props) => (props.$checked ? '#a7f3d0' : '#fecaca')};
`;

const UI_Input = styled(Input)`
  width: 100%;
  font-size: 13px;

  &:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }
`;

const UI_InputNumber = styled(InputNumber)`
  width: 100%;
  font-size: 13px;

  &:focus-within {
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }
`;

const UI_DatePicker = styled(DatePicker)`
  width: 100%;
  font-size: 13px;

  &:focus-within {
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }
`;

